/**
 * Max Priorities Pyramid tool runner.
 * Organizes items into pyramid segments by category, time horizon, and strategic direction.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Max Priorities Pyramid.
 */
export function getMaxPrioritiesPyramidSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("max-priorities-pyramid");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Max Priorities Pyramid tool.
 * 
 * @param items - Items to organize
 * @param _criteriaSet - CriteriaSet (not used)
 * @param toolConfig - Tool configuration (northStar, categories)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runMaxPrioritiesPyramidTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `max-priorities-pyramid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const northStar = (toolConfig.northStar as string) || "";

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Default values
  const defaultCategory = "Other Priorities";
  const defaultTimeHorizon = "Run";
  const defaultStrategicDirection = "Get House in Order";

  // Valid enum values
  const validTimeHorizons = ["Transform", "Grow", "Run"];
  const validStrategicDirections = [
    "Get House in Order",
    "Reinforce Foundation",
    "Create the Future",
  ];

  // Organize items into segments
  const segmentMap = new Map<
    string,
    Array<{ item: Item; rank: number; category: string; timeHorizon: string; strategicDirection: string }>
  >();

  for (const item of filteredItems) {
    // Extract attributes with defaults
    let category = item.attributes.category as string;
    if (!category || typeof category !== "string") {
      category = defaultCategory;
    }

    let timeHorizon = item.attributes.timeHorizon as string;
    if (!timeHorizon || typeof timeHorizon !== "string" || !validTimeHorizons.includes(timeHorizon)) {
      timeHorizon = defaultTimeHorizon;
    }

    let strategicDirection = item.attributes.strategicDirection as string;
    if (
      !strategicDirection ||
      typeof strategicDirection !== "string" ||
      !validStrategicDirections.includes(strategicDirection)
    ) {
      strategicDirection = defaultStrategicDirection;
    }

    // Get rank (default to 0)
    let rank = item.attributes.rank;
    if (rank === null || rank === undefined) {
      rank = 0;
    }
    const rankNum = typeof rank === "number" ? rank : Number(rank);
    const finalRank = isNaN(rankNum) ? 0 : rankNum;

    // Create segment key
    const segmentKey = `${category}-${timeHorizon}-${strategicDirection}`;

    if (!segmentMap.has(segmentKey)) {
      segmentMap.set(segmentKey, []);
    }

    segmentMap.get(segmentKey)!.push({
      item,
      rank: finalRank,
      category,
      timeHorizon,
      strategicDirection,
    });
  }

  // Sort items within each segment by rank (ascending), then by label
  for (const itemsInSegment of segmentMap.values()) {
    itemsInSegment.sort((a, b) => {
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      return a.item.label.localeCompare(b.item.label);
    });
  }

  // Build output
  const organizedItems: Array<{
    item_id: string;
    segment_key: string;
    category: string;
    timeHorizon: string;
    strategicDirection: string;
    rank: number;
    rank_within_segment: number;
  }> = [];
  const segmentSummary: Record<string, number> = {};

  for (const [segmentKey, itemsInSegment] of segmentMap.entries()) {
    segmentSummary[segmentKey] = itemsInSegment.length;

    for (let i = 0; i < itemsInSegment.length; i++) {
      const { item, rank, category, timeHorizon, strategicDirection } = itemsInSegment[i];

      organizedItems.push({
        item_id: item.id,
        segment_key: segmentKey,
        category: category,
        timeHorizon: timeHorizon,
        strategicDirection: strategicDirection,
        rank: rank,
        rank_within_segment: i + 1,
      });
    }
  }

  return {
    items: organizedItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      north_star: northStar,
      segment_summary: segmentSummary,
    },
  };
}


